import type { DisciplioSettings } from "./types";

export const MAX_CUSTOM_METRIC_UNITS = 10;

function defaultCurrencyUnitsForLanguage(language: string): string[] {
  if (language === "uk") return ["UAH", "EUR", "USD", "PLN"];
  if (language === "pl") return ["PLN", "EUR", "USD", "UAH"];
  return ["USD", "EUR", "PLN", "UAH"];
}

export function defaultMetricUnitsForLanguage(language: string): string[] {
  const currencyUnits = defaultCurrencyUnitsForLanguage(language);
  if (language === "pl") {
    return [
      "godziny",
      "minuty",
      "strony",
      "książki",
      "treningi",
      "sesje",
      "zadania",
      "etap",
      ...currencyUnits,
    ];
  }
  if (language === "uk") {
    return [
      "години",
      "хвилини",
      "сторінки",
      "книги",
      "тренування",
      "сесії",
      "задачі",
      "рази",
      "етап",
      ...currencyUnits,
    ];
  }
  return [
    "hours",
    "minutes",
    "pages",
    "books",
    "workouts",
    "sessions",
    "tasks",
    "milestone",
    "Days",
    ...currencyUnits,
  ];
}

export function normalizeMetricUnits(
  values: unknown,
  max = MAX_CUSTOM_METRIC_UNITS
): string[] {
  if (!Array.isArray(values)) return [];
  const seen = new Set<string>();
  const result: string[] = [];
  values.forEach((value) => {
    const unit =
      typeof value === "string" ? value.trim().replace(/\s+/g, " ") : "";
    const key = unit.toLowerCase();
    if (!unit || unit.length > 40 || seen.has(key) || result.length >= max)
      return;
    seen.add(key);
    result.push(unit);
  });
  return result;
}

export function mergeMetricUnits(
  defaultUnits: string[],
  customUnits: string[]
): string[] {
  return normalizeMetricUnits([...defaultUnits, ...customUnits], 30);
}

type UnitTheme = "focus" | "reading" | "fitness" | "finance" | "practice";

const TITLE_KEYWORDS: Record<UnitTheme, string[]> = {
  focus: [
    "bug",
    "code",
    "build",
    "project",
    "feature",
    "work",
    "task",
    "zad",
    "błąd",
    "projekt",
    "робот",
    "помил",
    "проєкт",
  ],
  reading: [
    "read",
    "book",
    "article",
    "study",
    "learn",
    "czyt",
    "książ",
    "nauk",
    "чит",
    "книг",
    "навч",
  ],
  fitness: [
    "workout",
    "run",
    "gym",
    "train",
    "exercise",
    "biega",
    "trening",
    "ćwicz",
    "біг",
    "тренув",
    "вправ",
  ],
  finance: [
    "save",
    "budget",
    "revenue",
    "income",
    "money",
    "oszczęd",
    "budżet",
    "przych",
    "заощад",
    "бюджет",
    "дохід",
  ],
  practice: [
    "habit",
    "meditat",
    "practice",
    "language",
    "routine",
    "nawyk",
    "medyt",
    "język",
    "звич",
    "медит",
    "мов",
  ],
};

const UNIT_KEYWORDS: Record<UnitTheme, string[]> = {
  focus: [
    "hour",
    "godzin",
    "годин",
    "session",
    "sesj",
    "сесі",
    "task",
    "zada",
    "зада",
  ],
  reading: [
    "page",
    "stron",
    "сторін",
    "book",
    "książ",
    "книг",
    "hour",
    "godzin",
    "годин",
  ],
  fitness: [
    "workout",
    "trening",
    "тренув",
    "minute",
    "minut",
    "хвилин",
    "session",
    "sesj",
    "сесі",
  ],
  finance: ["usd", "eur", "pln", "uah"],
  practice: [
    "session",
    "sesj",
    "сесі",
    "minute",
    "minut",
    "хвилин",
    "day",
    "dni",
    "дн",
  ],
};

/**
 * Returns existing metric units in an order that fits the goal title. A linked
 * parent unit always comes first, keeping measurable goals compatible with
 * their parent progress metric.
 */
export function recommendedMetricUnits(
  availableUnits: string[],
  title: string,
  parentUnit = ""
): string[] {
  const units = normalizeMetricUnits([parentUnit, ...availableUnits], 30);
  const normalizedTitle = title.trim().toLocaleLowerCase();
  const matchingThemes = (Object.keys(TITLE_KEYWORDS) as UnitTheme[]).filter(
    (theme) =>
      TITLE_KEYWORDS[theme].some((keyword) => normalizedTitle.includes(keyword))
  );
  const matchingUnits = units.filter((unit) => {
    const normalizedUnit = unit.toLocaleLowerCase();
    return matchingThemes.some((theme) =>
      UNIT_KEYWORDS[theme].some((keyword) => normalizedUnit.includes(keyword))
    );
  });

  return normalizeMetricUnits([parentUnit, ...matchingUnits, ...units], 30);
}

export function settingsWithMetricUnits(
  settings: DisciplioSettings
): DisciplioSettings {
  const defaultMetricUnits = defaultMetricUnitsForLanguage(settings.language);
  const customMetricUnits = normalizeMetricUnits(settings.customMetricUnits);
  return {
    ...settings,
    defaultMetricUnits,
    customMetricUnits,
    metricUnits: mergeMetricUnits(defaultMetricUnits, customMetricUnits),
  };
}

export function appendCustomMetricUnit(
  settings: DisciplioSettings,
  unit: string
): DisciplioSettings {
  const normalizedUnit = unit.trim().replace(/\s+/g, " ");
  if (!normalizedUnit || normalizedUnit.length > 40)
    return settingsWithMetricUnits(settings);
  const current = settingsWithMetricUnits(settings);
  const alreadyKnown = current.metricUnits.some(
    (entry) => entry.toLowerCase() === normalizedUnit.toLowerCase()
  );
  if (
    alreadyKnown ||
    current.customMetricUnits.length >= MAX_CUSTOM_METRIC_UNITS
  ) {
    return current;
  }
  return settingsWithMetricUnits({
    ...current,
    customMetricUnits: [...current.customMetricUnits, normalizedUnit],
  });
}
